import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilterPanel from './FilterPanel';
import PanelReport from './panel-report';
import PanelClassifications from './panel-classifications';
import EditDistrictDialog from './EditDistrictDialog';
import { getUnitsByFeatures } from '../helpers/units/units';
import { convertFeaturesToLayerFormat } from '../helpers/utils/convert-features-to-layer-format';
import * as districtCache from '../helpers/districts/district-cache';
import Tabs from './Tabs';
import Tab from '../types/tab';
import PanelHeader from './panel-header';
import PanelByline from './PanelByline';
import Takeover from '../../takeover';
import { Button } from '../../forms';

const panelTabs = [
    { id: Tab.REPORT, label: 'Alla distrikt' },
    { id: Tab.CLASSIFICATIONS, label: 'Klassificeringar' }
];

const getSaveText = ({ uiStates = {}, allDistricts = [] }) => {
    if (uiStates.isSaving) {
        return 'Sparar distrikt...';
    }

    if (uiStates.isFinishedSaving) {
        return 'Distrikt sparades';
    }

    const dirtyDistrictsCount = allDistricts.filter(district => district?.isDirty).length;
    if (dirtyDistrictsCount > 0) {
        return `Spara ${dirtyDistrictsCount} distrikt`;
    }

    return 'Spara distrikt';
};

const MapPanel = ({
    state,
    mapUiLoading,
    handlers,
    match,
    type,
    classifications = [],
    classificationsLoading = false,
    reloadClassifications,
    fetch,
}) => {
    const [, setCacheRevision] = useState(0);
    const [activeTab, setActiveTab] = useState(Tab.REPORT);
    const [editDialogState, setEditDialogState] = useState({ isOpen: false, district: null });
    const [backDialogOpen, setBackDialogOpen] = useState(false);
    const panelContentRef = useRef(null);
    const activeDistrictId = match?.params?.id;
    const scenarioId = match?.params?.scenarioId ?? null;

    useEffect(() => {
        return districtCache.subscribe(() => setCacheRevision(r => r + 1));
    }, []);

    const allDistricts = districtCache.getAllDistrictsForScenario();
    const hasDirtyDistricts = allDistricts.some(district => district?.isDirty);
    const overviewHref =
        scenarioId != null
            ? type === 'foodservice'
                ? `/foodservice-scenario/${scenarioId}`
                : `/scenario/${scenarioId}`
            : null;

    const handleBackClick = (e) => {
        if (hasDirtyDistricts) {
            e.preventDefault();
            setBackDialogOpen(true);
        }
    };

    const handleNavigateToOverview = () => {
        if (overviewHref) {
            window.location.href = overviewHref;
        }
    };

    const handleSaveAndNavigate = async () => {
        await handlers.save();
        const stillDirty = districtCache.getAllDistrictsForScenario().some(d => d?.isDirty);
        if (!stillDirty) {
            handleNavigateToOverview();
        }
    };

    const activeDistrict = allDistricts.find(
        district => String(district?.id) === String(activeDistrictId)
    );

    const selectedAreas = activeDistrict?.selectedAreas || [];

    const demographicsLayerSelection = useMemo(
        () => convertFeaturesToLayerFormat(selectedAreas),
        [selectedAreas]
    );

    const allUnits = Array.isArray(state.allUnits) ? state.allUnits : [];

    const totalUnitsInAreas = useMemo(
        () => getUnitsByFeatures(selectedAreas, allUnits).length,
        [selectedAreas, allUnits]
    );

    useEffect(() => {
        if (!panelContentRef.current) return;
        panelContentRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }, [activeDistrictId, activeTab]);

    const openEditDialog = (district) => {
        if (!district) return;
        setEditDialogState({ isOpen: true, district });
    };

    const closeEditDialog = () => {
        setEditDialogState({ isOpen: false, district: null });
    };

    const handleSaveNameAndColor = ({ name, color }) => {
        if (!editDialogState.district?.id) return;
        handlers.updateDistrictNameAndColor(editDialogState.district.id, name, color);
    };

    return (
        <div className="create-district-map__panel-container">
            <div className="create-district-map__panel-container-inner">
                <div className="create-district-map__panel-container-content" ref={panelContentRef}>
                    {activeTab !== Tab.FILTER &&
                        <>
                            <PanelHeader
                                district={activeDistrict}
                                type={type}
                                activeTab={activeTab}
                                onSetActiveTab={(tab) => setActiveTab(tab)}
                                onOpenEdit={() => openEditDialog(activeDistrict)}
                                demographicsDistrictId={activeDistrict?.id ?? null}
                                demographicsLayerSelection={demographicsLayerSelection}
                                salesDistrictFilters={activeDistrict?.salesDistrictFilters}
                                isLoading={mapUiLoading}
                            />
                            <PanelByline text="Scenario" />
                            {type !== 'foodservice' && (
                                <Tabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={panelTabs} />
                            )}
                        </>
                    }

                    {activeTab === Tab.REPORT && (
                        <>
                            <div className="create-district-map__report-container">
                                <PanelReport 
                                    type={type}
                                    allDistricts={allDistricts}
                                    match={match}
                                    handlers={handlers}
                                    saveSingleDistrict={(district) => handlers.saveSingleDistrict(district)}
                                    isLoading={mapUiLoading}
                                />
                            </div>
                        </>
                    )}
                    {activeTab === Tab.FILTER && (
                        <FilterPanel 
                            {...state}
                            match={match}
                            units={state.units}
                            allUnits={allUnits}
                            totalUnitsInAreas={totalUnitsInAreas}
                            numberOfUnits={state.numberOfHits}
                            filters={activeDistrict?.salesDistrictFilters}
                            onApplyFilters={(units, filters) => handlers.applyFilters(units, filters)}
                            onApplyFiltersFromDistrict={(sourceDistrictId) => handlers.applyFiltersFromDistrict(sourceDistrictId)}
                            activeDistrictId={activeDistrict?.id}
                            activeDistrict={activeDistrict}
                            allDistricts={allDistricts}
                            type={type}
                            unitsVisible={state.uiStates.unitsVisibility}
                            onBack={() => setActiveTab(Tab.REPORT)}
                        />
                    )}
                    {activeTab === Tab.CLASSIFICATIONS && type !== 'foodservice' && (
                        <PanelClassifications
                            allDistricts={allDistricts}
                            match={match}
                            classifications={classifications}
                            isLoading={classificationsLoading}
                            reload={reloadClassifications}
                            fetch={fetch}
                        />
                    )}
                </div>
                {(activeTab !== Tab.FILTER && activeTab !== Tab.CLASSIFICATIONS) && (
                    <div className="create-district-map__panel-container-footer">
                        <a
                            className="create-district-map__back-to-overview"
                            href={overviewHref ?? '#'}
                            onClick={handleBackClick}
                        >
                            Tillbaka till översikt
                        </a>
                        <button className="create-district-map__save-button" type="button" onClick={() => handlers.save()}>
                            {getSaveText({ uiStates: state.uiStates, allDistricts })}
                        </button>
                    </div>
                )}
            </div>
            <EditDistrictDialog
                district={editDialogState.district}
                isOpen={editDialogState.isOpen}
                onClose={closeEditDialog}
                onSave={handleSaveNameAndColor}
                type={type}
            />
            <Takeover
                isOpen={backDialogOpen}
                toggleTakeover={() => setBackDialogOpen(false)}
                heading="OSPARADE ÄNDRINGAR"
                isLoading={
                    backDialogOpen &&
                    Boolean(state.uiStates?.isSaving || mapUiLoading)
                }
            >
                <div>
                    <p>
                        Du har osparade distrikt i det här scenariot. Innan du fortsätter till översikten vill du:
                    </p>
                    <div className="create-district-map__forms-footer">
                        <Button type="button" modifiers={{ 'report-save-btn': true, 'create-district-map__create': true, 'report-save-btn--centered': true }} onClick={handleSaveAndNavigate}>
                            Spara och fortsätt
                        </Button>
                        <Button
                            type="button"
                            modifiers={{ 'form__button--muted': true, 'form__button--centered': true }}
                            onClick={handleNavigateToOverview}
                        >
                            Fortsätt utan att spara
                        </Button>
                    </div>
                </div>
            </Takeover>
        </div>
    );
};

export default MapPanel;

